import * as React from 'react';
import {FC, JSX, useEffect, useRef, useState} from 'react';
import {TabulatorFull as Tabulator, ColumnDefinition, RowComponent} from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator.min.css';
import {GoodNewsManageI} from "../model/GoodNewsManageI.ts";
import {GoodNews} from "../api/GoodNews.ts";
import Profile from "../../../Model/Profile.ts";
import css from '../GoodNews.module.css';
import Box from "@mui/material/Box";
import clsx from "clsx";
import {GoodNewsForm} from "./GoodNewsForm.tsx";
import {GoodNewsActivityI} from "../model/GoodNewsActivityI.ts";
import Swal from 'sweetalert2';


export interface GoodNewsProps {
    token: string;
    profile: Profile;
}

export const GoodNewsTable: FC<GoodNewsProps> = (props): JSX.Element => {
    const tableRef = useRef<HTMLDivElement | null>(null);
    const tabulatorRef = useRef<Tabulator | null>(null);
    const isTableBuilt = useRef<boolean>(false);
    const goodsNewsApi = new GoodNews(props.token, props.profile);

    const [goods, setGoods] = useState<GoodNewsManageI[]>([]);
    const [originalGood, setOriginalGood] = useState<GoodNewsManageI | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [editedRow, setEditedRow] = useState<RowComponent | null>(null);

    const [imagePreview, setImagePreview] = useState<{
        src: string;
        x: number;
        y: number;
        width: number;
        height: number
    } | null>(null);

    const handleEditClick = (row: RowComponent) => {
        setEditedRow(row);
        const rowData = row.getData() as GoodNewsManageI;
        setOriginalGood(rowData);
        setEditDialogOpen(true);

        row.getElement().classList.add(css.editedRow);
        row.reformat();
    };

    const deleteMsg = (epid: string, msg: string) : string => {
        return `
            <div>
                Usuwanie dobra przedsiębiorstwa:
                <p >
                    <strong>
                        EPID: ${epid}
                    </strong>
                </p>
                <p style="margin-top: 10px">
                    ${msg ? msg : "Brak tytułu."}
                </p>                
            </div>

        `;

    }

    const handleDeleteClick = async (row: RowComponent) => {

        const rowData = row.getData() as GoodNewsManageI;
        const rowElement = row.getElement()

        rowElement.classList.add("row-delete");
        const result = await Swal.fire({
            title: 'Czy na pewno chcesz usunąć?',
            html: deleteMsg(rowData.epid, rowData.shortDescription),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Tak, usuń!',
            cancelButtonText: 'Nie, zostaw',
            reverseButtons: true
        });

        if (result.isConfirmed) {

            try {
                await goodsNewsApi.markGoodDeleted(rowData.goodId);
                await row.delete();

                Swal.fire({
                    title: 'Usunięto!',
                    text: 'Rekord został pomyślnie skasowany.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false
                });

            } catch (error) {

                console.error("Błąd podczas usuwania:", error);

                Swal.fire(
                    'Błąd!',
                    'Nie udało się usunąć rekordu na serwerze.',
                    'error'
                );
            }
        } else {
            console.log("Anulowano usuwanie wiersza o ID:", rowData.shortDescription);
        }
        rowElement.classList.remove("row-delete");
    };


    const handleFormConfirm = (newGood: GoodNewsActivityI): void => {
        console.log(newGood);

        editedRow?.update(newGood);

        clearAfterEdit();
    }


    const handleFormCancel = () => {

        clearAfterEdit();
    }

    const clearAfterEdit = () =>  {
        const editedRowElement = editedRow?.getElement?.();

        setEditDialogOpen(false);
        setEditedRow(null);
        setOriginalGood(null);
        setImagePreview(null);

        if (editedRowElement && css.editedRow) {
            setTimeout(() => {
                if (editedRowElement.isConnected) {
                    editedRowElement.classList.remove(css.editedRow);
                }
            }, 3000);
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await goodsNewsApi.getGoodPromotions();
                setGoods(data);
            } catch (e) {
                console.log(e);
            }
        };

        fetchData();
    }, []);

    const columns: ColumnDefinition[] = [
        {
            title: "EPID",
            field: "epid",
            sorter: "string",
            width: 150,
            resizable:true
        },
        {
            title: "Aktywny",
            field: "active",
            sorter: "boolean",
            width: 100,
            formatter: "tickCross",
            hozAlign: "center",
            resizable:true
        },
        {
            title: "Data wyświetlenia",
            field: "lastPromotionDate",
            sorter: "date",
            width: 180,
            hozAlign: "center",
            resizable:true
        },
        {
            title: "Streszczenie",
            field: "shortDescription",
            sorter: "string",
            hozAlign: "left",
            formatter: "textarea",
            resizable:true
        },
        {
            title: "Artykuł",
            field: "description",
            formatter: "textarea",
            resizable:true
        },
        {
            title: "Grafika",
            field: "image",
            width: 150,
            hozAlign: "center",
            formatter: (cell) => {
                const value = cell.getValue();
                if (!value) {
                    return "";
                }
                const container = document.createElement("div");
                container.style.display = "flex";
                container.style.justifyContent = "center";
                container.style.alignItems = "center";
                container.style.width = "100%";
                container.style.height = "100%";
                container.style.padding = "5px";

                const img = document.createElement("img");
                img.src = value;
                img.style.minWidth = "100px";
                img.style.minHeight = "100px";
                img.style.maxWidth = "100px";
                img.style.maxHeight = "100px";
                img.style.objectFit = "contain";
                img.style.cursor = "pointer";

                let cachedDimensions: { width: number; height: number } | null = null;

                img.onload = () => {
                    const previewHeight = window.innerHeight * 0.3;
                    const aspectRatio = img.naturalWidth / img.naturalHeight;
                    const previewWidth = previewHeight * aspectRatio;
                    cachedDimensions = {width: previewWidth, height: previewHeight};
                };

                const updatePreview = (e: MouseEvent) => {
                    if (!cachedDimensions) {
                        const previewHeight = window.innerHeight * 0.3;
                        const aspectRatio = img.naturalWidth / img.naturalHeight;
                        const previewWidth = previewHeight * aspectRatio;
                        cachedDimensions = {width: previewWidth, height: previewHeight};
                    }

                    let posY = e.clientY + 20;
                    if (posY + cachedDimensions.height > window.innerHeight) {
                        posY = window.innerHeight - cachedDimensions.height - 10;
                    }
                    if (posY < 10) {
                        posY = 10;
                    }

                    setImagePreview({
                        src: value,
                        x: e.clientX,
                        y: posY,
                        width: cachedDimensions.width,
                        height: cachedDimensions.height
                    });
                };

                img.addEventListener("mouseenter", updatePreview);
                img.addEventListener("mousemove", updatePreview);
                img.addEventListener("mouseleave", () => {
                    setImagePreview(null);
                });

                container.appendChild(img);
                return container;
            }
        },
        {
            title: "Edytuj",
            field: "edit",
            width: 100,
            hozAlign: "center",
            formatter: () => "<button class='edit-btn'>Edytuj</button>",
            cellClick: (_e, cell) => handleEditClick(cell.getRow()),
        },
        {
            title: "Usuń",
            width:100,
            hozAlign: "center",
            formatter: () => "<button class='delete-btn'>Usuń</button>",
            cellClick: (_e, cell) => handleDeleteClick(cell.getRow()),
        }
    ];


    useEffect(() => {
        let frameId: number | null = null;
        isTableBuilt.current = true;

        if (tableRef.current) {
            frameId = window.requestAnimationFrame(() => {
                if (!tableRef.current) {
                    return;
                }

                const table = new Tabulator(tableRef.current, {
                    data: goods,
                    index: "goodId",
                    columns,
                    layout: "fitColumns",
                    resizableColumnFit:true,
                    height: "100%",
                    maxHeight: "100%",
                    pagination: true,
                    paginationSize: 50,
                    movableColumns: true,
                    renderVertical:"basic",
                    rowFormatter: (row) => {
                        const rowData = row.getData();
                        if (rowData.image) {
                            row.getElement().style.height = "100px";
                        }

                        if (rowData.note) {
                            const rowElement = row.getElement();

                            let noteRow = rowElement.querySelector(".row-note-container");

                            if (!noteRow) {
                                noteRow = document.createElement("div");
                                noteRow.classList.add("row-note-container");
                                rowElement.appendChild(noteRow);
                            }

                            const noteText = rowData.note ? rowData.note : "Brak dodatkowych uwag.";
                            noteRow.innerHTML = `${noteText}`;
                        }
                    }
                });

                table.on("tableBuilt", () => {
                    isTableBuilt.current = true;
                    tabulatorRef.current = table;
                });
            });
        }

        return () => {
            if (frameId) {
                window.cancelAnimationFrame(frameId);
            }

            isTableBuilt.current = false;

            if (tabulatorRef.current) {
                tabulatorRef.current.destroy();
                tabulatorRef.current = null;
            }
        };
    }, []);


    useEffect(() => {
        if (isTableBuilt.current && tabulatorRef.current) {
            tabulatorRef.current.setData(goods).catch(() => {
            });
        }
    }, [goods]);


    return (

        <Box className={clsx(css.goodNews)}>

            <div className={clsx(css.tableContainer)} ref={tableRef}/>

            {imagePreview && (
                <div
                    style={{
                        position: "fixed",
                        right: `${window.innerWidth - imagePreview.x + 20}px`,
                        top: `${imagePreview.y}px`,
                        zIndex: 10000,
                        pointerEvents: "none",
                        backgroundColor: "white",
                        border: "2px solid #ccc",
                        borderRadius: "4px",
                        padding: "5px",
                        boxShadow: "0 4px 8px rgba(0,0,0,0.3)"
                    }}
                >
                    <img
                        src={imagePreview.src}
                        alt="Preview"
                        style={{
                            height: `${imagePreview.height}px`,
                            width: `${imagePreview.width}px`,
                            display: "block"
                        }}
                    />
                </div>
            )}


            {editDialogOpen && originalGood && (
                <GoodNewsForm
                    goodsNewsApi={goodsNewsApi}
                    originalGood={originalGood}
                    formConfirm={handleFormConfirm}
                    formCancel={handleFormCancel}
                />
            )}
        </Box>
    );
};
